import { motion } from "framer-motion";
import {
    AlertTriangle,
    FileSpreadsheet,
    Mail,
    MessagesSquare,
    Sparkles,
    Activity,
    GitBranch,
    LineChart,
} from "lucide-react";

const chaos = [
    { icon: FileSpreadsheet, label: "Sheets stitched by hand" },
    { icon: Mail, label: "Updates buried in inboxes" },
    { icon: MessagesSquare, label: "Decisions lost in DMs" },
    { icon: AlertTriangle, label: "Numbers that never agree" },
];

const intelligence = [
    { icon: GitBranch, label: "End-to-end business visibility" },
    { icon: Activity, label: "Connected metrics across every function" },
    { icon: LineChart, label: "Predictive insights powered by real-time data" },
    { icon: Sparkles, label: "AI that identifies bottlenecks before they impact growth." },
];

export const BeforeAfter = () => {
    return (
        <section
            id="transformation"
            className="relative py-24 sm:py-32 overflow-hidden"
            data-testid="before-after-section"
        >
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
            <div className="absolute inset-0 grid-bg opacity-[0.18] pointer-events-none" />

            <div className="relative max-w-7xl mx-auto px-6 sm:px-8">
                <div className="max-w-3xl">
                    <p className="text-xs uppercase tracking-[0.25em] text-amber-glow font-semibold mb-4 font-code">
                        // the transformation
                    </p>
                    <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-[1.05]">
                        From <span className="text-zinc-500 line-through decoration-zinc-700">operational chaos</span>
                        <br />
                        to{" "}
                        <span className="gradient-text">organisational intelligence.</span>
                    </h2>
                    <p className="mt-6 text-zinc-400 text-base sm:text-lg font-light leading-relaxed">
                        Most companies don't have a software problem — they have a
                        coordination problem. Twelve tools, four spreadsheets and a
                        Monday morning standup is not a system. ApexBrain replaces the
                        stack with a single living surface.
                    </p>
                </div>

                <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 relative">
                    {/* connector arrow */}
                    <div className="hidden lg:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20 items-center justify-center w-14 h-14 rounded-full bg-obsidian-300 border border-white/10 shadow-[0_0_40px_rgba(94,234,212,0.25)]">
                        <span className="font-code text-[10px] text-cyan-300 font-semibold tracking-widest">→</span>
                    </div>

                    {/* BEFORE */}
                    <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, margin: "-50px" }}
                        transition={{ duration: 0.6 }}
                        className="relative rounded-2xl border border-white/[0.06] bg-obsidian-200/60 p-7 sm:p-9 backdrop-blur-sm overflow-hidden"
                        data-testid="before-card"
                    >
                        <div className="absolute -top-16 -right-16 w-48 h-48 bg-red-500/10 blur-3xl rounded-full pointer-events-none" />
                        <div className="relative flex items-center gap-3 mb-2">
                            <span className="text-[10px] font-code uppercase tracking-[0.2em] text-red-300 bg-red-500/10 border border-red-500/20 rounded-full px-2 py-0.5">
                                Before · v0
                            </span>
                            <span className="text-[10px] font-code text-zinc-600">{`status = fragmented`}</span>
                        </div>
                        <h3 className="font-outfit text-2xl font-semibold text-white">
                            A company run on tabs.
                        </h3>
                        <p className="mt-2 text-sm text-zinc-500 font-light leading-relaxed">
                            Context lives in 14 places. Reality lives in nobody's head.
                        </p>

                        <ul className="mt-7 space-y-2.5">
                            {chaos.map((c, i) => (
                                <motion.li
                                    key={c.label}
                                    initial={{ opacity: 0, x: -10 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: 0.1 + i * 0.06 }}
                                    className="flex items-center gap-3 text-sm text-zinc-400"
                                    data-testid={`chaos-row-${i}`}
                                >
                                    <span className="shrink-0 w-7 h-7 rounded-md bg-white/[0.03] border border-white/[0.06] flex items-center justify-center">
                                        <c.icon className="w-3.5 h-3.5 text-zinc-500" />
                                    </span>
                                    <span className="line-through decoration-zinc-700/80">
                                        {c.label}
                                    </span>
                                </motion.li>
                            ))}
                        </ul>
                    </motion.div>

                    {/* AFTER */}
                    <motion.div
                        initial={{ opacity: 0, x: 30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, margin: "-50px" }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="relative rounded-2xl border border-electric/30 bg-gradient-to-br from-obsidian-100 to-obsidian-200 p-7 sm:p-9 overflow-hidden shadow-[0_30px_80px_-30px_rgba(59,130,246,0.5)]"
                        data-testid="after-card"
                    >
                        <div className="absolute -top-24 -right-24 w-64 h-64 bg-electric/20 blur-3xl rounded-full pointer-events-none" />
                        <div className="relative flex items-center gap-3 mb-2">
                            <span className="text-[10px] font-code uppercase tracking-[0.2em] text-emerald-300 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
                                <span className="w-1 h-1 rounded-full bg-emerald-400 animate-pulse-glow" />
                                After · ApexBrain
                            </span>
                            <span className="text-[10px] font-code text-zinc-500">{`status = unified`}</span>
                        </div>
                        <h3 className="font-outfit text-2xl font-semibold text-white">
                            A company run as a system.
                        </h3>
                        <p className="mt-2 text-sm text-zinc-400 font-light leading-relaxed">
                            Every function, every metric, every signal — one connected surface.
                        </p>

                        <ul className="mt-7 space-y-2.5">
                            {intelligence.map((c, i) => (
                                <motion.li
                                    key={c.label}
                                    initial={{ opacity: 0, x: 10 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: 0.15 + i * 0.06 }}
                                    className="flex items-center gap-3 text-sm text-zinc-200"
                                    data-testid={`intelligence-row-${i}`}
                                >
                                    <span className="shrink-0 w-7 h-7 rounded-md bg-electric/10 border border-electric/30 flex items-center justify-center">
                                        <c.icon className="w-3.5 h-3.5 text-electric" />
                                    </span>
                                    <span>{c.label}</span>
                                </motion.li>
                            ))}
                        </ul>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};
